/**
 * Pure helpers shared by all three frontends.
 *
 * Anything here is a rule that must not be re-implemented per app: the moment
 * the website and the QR menu each grow their own price formatter, they start
 * disagreeing about a currency or a decimal place in front of a guest.
 */
export const LOCALES = ['ar', 'en'];
/** Arabic is the fallback locale on the API, so it leads here too. */
export const DEFAULT_LOCALE = 'ar';
export function isLocale(value) {
    return LOCALES.includes(value);
}
export function isRtl(locale) {
    return locale === 'ar';
}
export function directionOf(locale) {
    return isRtl(locale) ? 'rtl' : 'ltr';
}
/** The other language. Useful for the side-by-side editor. */
export function otherLocale(locale) {
    return locale === 'ar' ? 'en' : 'ar';
}
// ------------------------------------------------------------- localization
/**
 * Read one language out of a pair.
 *
 * **There is deliberately no fallback.** Required fields need both languages
 * before a record can be published, so a published record always has the one
 * being asked for. An optional field renders only in the language present —
 * showing English prose on an Arabic page is worse than showing nothing, and
 * it is the exact failure the publish-time validation exists to prevent.
 */
export function localized(pair, locale) {
    if (!pair)
        return '';
    return pair[locale] ?? '';
}
/** True when this language has something to show. */
export function hasLocale(pair, locale) {
    return localized(pair, locale).trim().length > 0;
}
/** True when either language is empty — what the editor's gap badge reads. */
export function isPartiallyTranslated(pair) {
    if (!pair)
        return false;
    const ar = hasLocale(pair, 'ar');
    const en = hasLocale(pair, 'en');
    return ar !== en;
}
export function emptyPair() {
    return { ar: '', en: '' };
}
/**
 * A nullable pair as an editable one.
 *
 * The API sends `null` for a column nobody has written, and an editor needs an
 * empty box rather than the word "null". This is the one place that conversion
 * happens, so no screen has to remember it.
 */
export function editablePair(pair) {
    return { ar: pair?.ar ?? '', en: pair?.en ?? '' };
}
// -------------------------------------------------------------------- money
/**
 * Format a decimal string as money.
 *
 * The amount stays a string all the way from Postgres to here; it is converted
 * to a number only inside `Intl.NumberFormat`, at the last possible moment and
 * never written back.
 *
 * `currency` is null until the owner sets one, and the API refuses to publish
 * anything priced before then — so a null here means "not configured yet"
 * rather than "free", and the caller is told apart from a null `amount`.
 *
 * The numbering system is left to the locale. Whether Arabic shows `١٢٣` or
 * `123` is still an open question for the owner; when it is answered, this is
 * the one function that changes.
 */
export function formatMoney(amount, currency, locale) {
    if (amount === null)
        return null;
    const value = Number(amount);
    if (!Number.isFinite(value))
        return null;
    if (!currency) {
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
}
/**
 * The same formatting as `formatMoney`, split so the currency can be drawn.
 *
 * Saudi Arabia adopted a new riyal symbol in 2025 and no shipping font has a
 * glyph for it, so `Intl` can only write "SAR" or "ر.س.". Rendering the mark
 * means replacing that run with a drawn one — and the thing that must not be
 * reimplemented while doing it is *where the run goes*: before the number in
 * English, after it in Arabic, with or without a space. `Intl` is the only
 * thing that knows, so the split happens here and the caller only chooses what
 * to paint.
 *
 * Adjacent non-currency runs are merged, so a caller gets at most one element
 * to swap and the rest as plain text.
 */
export function formatMoneyParts(amount, currency, locale) {
    if (amount === null)
        return null;
    const value = Number(amount);
    if (!Number.isFinite(value))
        return null;
    const format = new Intl.NumberFormat(locale, {
        ...(currency ? { style: 'currency', currency } : {}),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const parts = [];
    for (const part of format.formatToParts(value)) {
        const type = part.type === 'currency' ? 'currency' : 'text';
        const previous = parts[parts.length - 1];
        if (previous && previous.type === type)
            previous.value += part.value;
        else
            parts.push({ type, value: part.value });
    }
    return parts;
}
/**
 * Whether this currency is rendered as a drawn mark rather than as text.
 *
 * One place decides, because three surfaces ask. Everything else keeps using
 * the string `Intl` produces.
 */
export function isDrawnCurrency(currency) {
    return currency === 'SAR';
}
/** True when a price is set and zero — "free", which reads differently. */
export function isFree(amount) {
    return amount !== null && Number(amount) === 0;
}
/**
 * The saving on a discounted item, as a whole percent.
 *
 * Returns null when either price is missing or the "discount" is not one —
 * a promotional price at or above the regular price is an editing mistake the
 * API already refuses, and no UI should render it as a saving.
 */
export function discountPercent(regular, promotional) {
    if (regular === null || promotional === null)
        return null;
    const from = Number(regular);
    const to = Number(promotional);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from <= 0 || to >= from)
        return null;
    return Math.round(((from - to) / from) * 100);
}
// --------------------------------------------------------------------- time
/**
 * Format an "HH:mm" clock time for display.
 *
 * Takes the string apart rather than parsing a Date, because these times have
 * no date and no zone — attaching today's date to them would make a DST day
 * render an hour wrong.
 */
export function formatClock(time, locale, hour12 = false) {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
    if (!match)
        return time;
    const date = new Date(Date.UTC(2000, 0, 1, Number(match[1]), Number(match[2])));
    return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12,
        timeZone: 'UTC',
    }).format(date);
}
/** ISO weekday 1–7, Monday first, matching the hours model. */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
export function formatWeekday(weekday, locale, style = 'long') {
    // 2024-01-01 was a Monday, so ISO weekday N is that date plus N-1 days.
    const date = new Date(Date.UTC(2024, 0, weekday));
    return new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(date);
}
export function formatDate(iso, locale, timeZone) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        ...(timeZone ? { timeZone } : {}),
    }).format(date);
}
export function formatDateTime(iso, locale, timeZone) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        ...(timeZone ? { timeZone } : {}),
    }).format(date);
}
/**
 * A phone number as a dialable `tel:` URI.
 *
 * Numbers are stored the way an owner types them — `+963 11 123 4567` — and
 * that string went straight into `href="tel:…"` on five surfaces. RFC 3966 has
 * no room for spaces there; most dialers forgive it, and forgiveness is not a
 * specification. `+` is kept because it is what makes the number international.
 */
export function telHref(phone) {
    return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
/**
 * A phone number as a wa.me link.
 *
 * Digits only, including no `+`: wa.me takes a bare international number as a
 * path segment, and anything else is a 404 rather than a chat.
 */
export function whatsappHref(phone) {
    return `https://wa.me/${phone.replace(/\D/g, '')}`;
}
// ----------------------------------------------------------- opening hours
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
/**
 * Settle `closesNextDay` from the two times, where the two times settle it.
 *
 * On the wire the flag stays explicit, and that is right: 18:00 → 18:00 is
 * genuinely ambiguous — a zero-length mistake, or a full twenty-four hours —
 * and the model should not guess. But a close time *strictly earlier* than its
 * open time is not ambiguous at all. 07:00 → 01:00 can only mean the following
 * morning, which is a normal evening for a cafe.
 *
 * Making an editor tick a second control to say so is what turned that evening
 * into "that schedule is not valid", with nothing on screen pointing at the
 * checkbox that would have fixed it. So the editor derives it, and the stored
 * model keeps saying it out loud.
 *
 * Lexicographic order on "HH:mm" is chronological order, so the comparison is
 * the whole rule. A half-typed or cleared field decides nothing: the interval
 * is returned untouched and the API's own INVALID_TIME has the last word.
 */
export function withDerivedNextDay(interval) {
    if (!HHMM.test(interval.opensAt) || !HHMM.test(interval.closesAt))
        return interval;
    if (interval.opensAt === interval.closesAt)
        return interval;
    return { ...interval, closesNextDay: interval.closesAt < interval.opensAt };
}
// ---------------------------------------------------------------- ordering
/**
 * Move one entry within the complete order a reorder has to submit.
 *
 * A reorder is a claim about a whole list, and the API refuses one that does
 * not name every live row exactly once. A list screen holds nothing like that:
 * it pages at twenty, it narrows to whatever filter is set, and — with no
 * status filter — it includes archived rows the API does not count. Assembling
 * the payload from the rows that happened to be on screen therefore failed
 * three separate ways, and the screen could not tell which.
 *
 * So `live` is the order the server keeps and `visible` is the page in front of
 * the editor: "up" means *above the previous row you can see*, and, when the
 * page simply ends there, above the previous row there is. Both read the same
 * for one unfiltered page, which is the only case a plain swap ever handled.
 */
export function movedWithin(live, visible, id, delta) {
    const from = live.indexOf(id);
    if (from < 0)
        return live;
    const seen = visible.indexOf(id);
    const candidates = [seen >= 0 ? visible[seen + delta] : undefined, live[from + delta]];
    let to = -1;
    for (const candidate of candidates) {
        if (candidate === undefined)
            continue;
        const index = live.indexOf(candidate);
        if (index >= 0) {
            to = index;
            break;
        }
    }
    if (to < 0)
        return live;
    // Removing first and splicing at the neighbour's ORIGINAL index lands the row
    // before that neighbour going up and after it going down, in one expression.
    const next = live.filter((entry) => entry !== id);
    next.splice(to, 0, id);
    return next;
}
//# sourceMappingURL=helpers.js.map